/**
 * Contacts CSV export for Person entities.
 *
 * Uses the column names mailing-list tools auto-map on import — "Email
 * Address" (required), "First Name", "Last Name" — first, then one column per
 * enabled custom Person field (headed by its label), which the admin maps
 * during import. Rows without an email are unimportable, so they're skipped
 * and reported rather than emitted.
 *
 *   const { csv, exported, skipped, skippedEntities } = buildContactsCsv(entities, personFields);
 *   downloadCsv(csv, 'vellby-contacts.csv');
 */

// Vellby stores a single display name; the export splits it. First token
// is the first name, the rest (possibly multi-word) is the last name.
function splitName(name) {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    return [parts[0] || '', parts.slice(1).join(' ')];
}

const placeLabel = p => (p && typeof p === 'object' && p.lat != null && p.lng != null
    ? (p.label || `${p.lat}, ${p.lng}`)
    : '');

function formatFieldValue(field, raw) {
    if (raw === undefined || raw === null || raw === '') return '';
    if (field.field_type === 'date') return String(raw).slice(0, 10); // YYYY-MM-DD, the widely accepted import format
    if (field.field_type === 'geo') return placeLabel(raw);
    // Several places in one cell; "; " since labels themselves contain commas.
    if (field.field_type === 'geo_multi') return (Array.isArray(raw) ? raw : []).map(placeLabel).filter(Boolean).join('; ');
    return String(raw);
}

function escapeCell(value) {
    let s = String(value ?? '');
    // Neutralise spreadsheet formula injection if the file is opened in Excel/Sheets.
    if (/^[=@]/.test(s)) s = `'${s}`;
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function buildContactsCsv(entities, personFields = []) {
    const header = ['Email Address', 'First Name', 'Last Name', ...personFields.map(f => f.label)];
    const rows = [];
    const skippedEntities = [];
    for (const e of entities) {
        const email = (e.email || '').trim();
        if (!email) { skippedEntities.push(e); continue; }
        const [first, last] = splitName(e.name);
        rows.push([
            email,
            first,
            last,
            ...personFields.map(f => formatFieldValue(f, e.attributes?.[f.key])),
        ]);
    }
    const csv = [header, ...rows].map(r => r.map(escapeCell).join(',')).join('\r\n');
    return { csv, exported: rows.length, skipped: skippedEntities.length, skippedEntities };
}

export function downloadCsv(csv, filename) {
    // BOM so Excel opens UTF-8 names (accents, non-Latin scripts) correctly;
    // import tools ignore it.
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}
